import { useEffect, useState, type FormEvent } from 'react'
import { KeyRound } from 'lucide-react'
import { api } from '../../api/client'
import { PageHeader } from '../../components/PageHeader'
import { useI18n } from '../../i18n'

type OIDCConfig = { issuer: string; clientId: string; clientSecret: string; secretSet: boolean; redirectUrl: string; enabled: boolean }

export function SSOPage() {
  const { t } = useI18n()
  const [oidc, setOidc] = useState<OIDCConfig | null>(null)
  const [status, setStatus] = useState('')

  useEffect(() => {
    api.get<OIDCConfig>('/admin/oidc').then(setOidc).catch((e: Error) => setStatus(e.message))
  }, [])

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    try {
      const payload = {
        issuer: String(form.get('issuer')),
        clientId: String(form.get('clientId')),
        clientSecret: String(form.get('clientSecret')),
        redirectUrl: String(form.get('redirectUrl')),
        enabled: form.get('enabled') === 'on',
      }
      await api.patch('/admin/oidc', payload)
      setOidc({ ...payload, secretSet: payload.clientSecret !== '' || (oidc?.secretSet ?? false) })
      setStatus(t('admin.saveSettings') + ' ✓')
    } catch (e) { setStatus(e instanceof Error ? e.message : 'Error') }
  }

  return <>
    <PageHeader title="admin.ssoSettings" description="admin.ssoDescription" />
    {status && <p className="mb-5 rounded-lg bg-primary/10 px-4 py-2.5 text-sm text-primary">{status}</p>}

    <form key={oidc ? 'loaded' : 'loading'} onSubmit={save} className="panel max-w-2xl p-5 sm:p-6">
      <div className="flex items-center gap-2 mb-5">
        <KeyRound size={18} className="text-primary" />
        <h2 className="text-base font-semibold">OpenID Connect</h2>
      </div>

      <div className="space-y-5">
        <label className="flex items-center gap-2 text-sm">
          <input name="enabled" type="checkbox" className="rounded" defaultChecked={oidc?.enabled} />
          {t('admin.ssoEnabled')}
        </label>

        <label className="block text-sm font-medium">
          {t('admin.ssoIssuer')}
          <input className="field mt-1.5" name="issuer" defaultValue={oidc?.issuer} placeholder="https://sso.example.com" />
        </label>

        <label className="block text-sm font-medium">
          {t('admin.ssoClientId')}
          <input className="field mt-1.5" name="clientId" defaultValue={oidc?.clientId} />
        </label>

        <label className="block text-sm font-medium">
          {t('admin.ssoClientSecret')}
          <input className="field mt-1.5" name="clientSecret" type="password" placeholder={oidc?.secretSet ? '••••••••' : ''} />
          {oidc?.secretSet && <span className="mt-1 block text-xs text-muted-foreground">{t('admin.ssoSecretSet')}</span>}
        </label>

        <label className="block text-sm font-medium">
          {t('admin.ssoRedirectUrl')}
          <input className="field mt-1.5" name="redirectUrl" defaultValue={oidc?.redirectUrl} placeholder="https://mail.example.com/auth/callback" />
        </label>
      </div>

      <button className="btn-primary mt-6">{t('admin.saveSettings')}</button>
    </form>
  </>
}
